import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin } from '@repletoryreach/sdk-api';
import { entryMarkupPercent, plansOnSale } from '../lib/priceLadder';

export function useSkuRates() {
  return useQuery({
    queryKey: ['admin', 'sku-rates'],
    queryFn: () => admin.listSkuRates()
  });
}

/**
 * The plans as the price ladder reads them (21 September 2026): the ones on
 * sale in price-list order, and the markup of the entry (cheapest) plan, which
 * sets every list price and floor. It shares the plans page's cache.
 *
 * `entryMarkupPercent` is null until the plans have been read, so a dialog can
 * tell "no floor yet" from a floor and never previews a guess as a fact.
 */
export function usePriceLadder() {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['admin', 'plans'], queryFn: () => admin.listPlans() });
  return useMemo(() => {
    const all = data?.data;
    return {
      all: all ?? [],
      plans: plansOnSale(all ?? []),
      entryMarkupPercent: all ? entryMarkupPercent(all) : null,
      isLoading,
      isError,
      refetch
    };
  }, [data, isLoading, isError, refetch]);
}

/**
 * Re-price a SKU: the charge, and the platform's cost when given. The service
 * retires the current version and opens one effective today.
 */
export function useUpdateSkuRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: { id: string } & admin.UpdateSkuRateRequest) => admin.updateSkuRate(id, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sku-rates'] })
  });
}

export function useCreateSkuRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: admin.CreateSkuRateRequest) => admin.createSkuRate(req),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sku-rates'] })
  });
}

/** Read a Meta rate card and show what it would change. Writes nothing. */
export function usePreviewRateImport() {
  return useMutation({ mutationFn: (input: admin.RateImportInput) => admin.previewRateImport(input) });
}

/** Write the lines the operator confirmed; the price book and the import history refresh. */
export function useApplyRateImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: admin.ApplyRateImportRequest) => admin.applyRateImport(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'sku-rates'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'rate-imports'] });
    }
  });
}

export function useRateImports(limit = 5) {
  return useQuery({ queryKey: ['admin', 'rate-imports', limit], queryFn: () => admin.listRateImports(limit) });
}

/** Cancel a rate dated ahead, before anyone has been charged at it. */
export function useCancelUpcomingRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => admin.cancelUpcomingSkuRate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'sku-rates'] })
  });
}

export function useFxRates() {
  return useQuery({ queryKey: ['admin', 'fx-rates'], queryFn: () => admin.listFxRates() });
}

export function useUpdateFxRate() {
  const qc = useQueryClient();
  return useMutation({
    // A rate set by hand, the automatic-update switch for one currency, or both.
    mutationFn: ({ currency, change }: { currency: string; change: { per_zar?: number; auto_update?: boolean } }) => admin.updateFxRate(currency, change),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'fx-rates'] })
  });
}

/** Read the public exchange rate feeds now instead of waiting for the twelve-hourly pass. */
export function useRefreshFxRates() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => admin.refreshFxRates(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'fx-rates'] })
  });
}

/** The spread between mid-market and the two rates money converts at. */
export function useUpdateFxSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (spreadPercent: number) => admin.updateFxSettings(spreadPercent),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'fx-rates'] })
  });
}

/** Revenue against cost for a window; the default is the last 30 days. */
export function useUsageMargin(opts?: { since?: string; until?: string }) {
  return useQuery({
    queryKey: ['admin', 'usage-margin', opts?.since ?? null, opts?.until ?? null],
    queryFn: () => admin.usageMargin(opts)
  });
}
